import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  LinearProgress,
  Card,
  Alert,
  AlertTitle,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  People,
  PersonAdd,
  NavigateBefore,
  NavigateNext,
  Edit,
  Delete,
  Search,
  FilterList,
  VideoCall,
  Timeline,
  Assessment,
  TrendingUp,
} from '@mui/icons-material';
import { WeeklyTeamData, TeamMember } from '../types';
import { apiService } from '../services/api';
import { useBrandColors } from '../hooks/useBrandColors';
import { HuddleModal } from './HuddleModal';
import timezoneService from '../services/timezoneService';
import { DateService } from '../utils/dateService';

// Internal component for handling capacity updates gracefully
const CapacityInput: React.FC<{ 
  value: number; 
  onChange: (newValue: number) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => {
  const [localValue, setLocalValue] = useState(value.toString());

  useEffect(() => {
    setLocalValue(value.toString());
  }, [value]);

  const handleBlur = () => {
    const numVal = parseFloat(localValue);
    if (!isNaN(numVal) && numVal !== value) {
      onChange(numVal);
    } else {
      setLocalValue(value.toString()); // Revert if invalid
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const target = e.target as HTMLInputElement;
      target.blur(); // Triggers handleBlur
    }
  };

  return (
    <TextField
      size="small"
      type="number"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      inputProps={{ min: 0, max: 168, step: 0.5 }}
      sx={{ width: 80 }}
    />
  );
};

interface TeamManagerProps {}

// Team Activity Colors
const TEAM_COLORS = [
  '#3F51B5', '#F44336', '#E91E63', '#9C27B0', '#673AB7', 
  '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50', 
  '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800', '#FF5722'
];

const TeamManager: React.FC<TeamManagerProps> = () => {
  const brandColors = useBrandColors();
  const [weeklyData, setWeeklyData] = useState<any | null>(null); // Relaxed type for dailyStats
  const [selectedFilter, setSelectedFilter] = useState<string>('Everyone');
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    // eslint-disable-next-line no-restricted-syntax
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // If Sunday (0), go back 6 days, otherwise go to Monday
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: 'Member',
    capacity: 35
  });
  const [showEditMemberDialog, setShowEditMemberDialog] = useState(false);
  const [editingMember, setEditingMember] = useState<any>(null);
  const [editForm, setEditForm] = useState({
    role: 'member',
    costRate: '' as string | number,
    billRate: '' as string | number,
    currency: 'USD'
  });
  const [showHuddleModal, setShowHuddleModal] = useState(false);
  const [huddleChannelId] = useState('team-huddle');
  const [huddleChannelName] = useState('Team Huddle');
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);

  // Load team data from API
  const loadTeamData = useCallback(async () => {
    try {
      setLoading(true);
      
      const weekStartStr = currentWeekStart.toISOString().split('T')[0];
      console.log('🔄 loadTeamData called for week:', weekStartStr);
      
      const [teamMembers, teamStats] = await Promise.all([
        apiService.getTeamMembers(weekStartStr).catch((error) => {
          console.error('❌ Failed to load team members:', error);
          return [];
        }),
        apiService.getTeamStats(weekStartStr).catch((error) => {
          console.error('❌ Failed to load team stats:', error);
          return null;
        })
      ]);

      if (teamMembers && teamMembers.length > 0) {
        const memberData = teamMembers.map((member: any) => {
          const validRole: TeamMember['role'] = ['super_owner', 'workspace_owner', 'owner', 'admin', 'administrator', 'member', 'tester'].includes(member.role) 
            ? member.role as TeamMember['role'] 
            : 'member';
          
          return {
            member: {
              id: member.id,
              name: member.name || member.email,
              email: member.email,
              role: validRole,
              capacity: member.capacity || 35,
              avatar: member.avatar || undefined, // Use actual avatar or undefined (MUI Avatar shows initials as fallback)
              isActive: member.status === 'active',
              createdAt: member.createdAt || new Date().toISOString(),
              updatedAt: member.updatedAt || new Date().toISOString()
            },
            hoursWorked: member.hoursWorked || 0,
            billableHours: member.billableHours || 0,
            capacity: member.capacity || 35,
            utilizationPercentage: member.utilizationPercentage || 0,
            dailyStats: member.dailyStats || [] // Include dailyStats
          };
        });

        let finalStats;
        if (teamStats && typeof teamStats.totalHours === 'number') {
          finalStats = {
            totalHours: teamStats.totalHours || 0,
            teamCapacity: teamStats.teamCapacity || 35,
            billableHours: teamStats.billableHours || 0,
            nonBillableHours: teamStats.nonBillableHours || 0,
            utilizationPercentage: teamStats.utilizationPercentage || 0
          };
        } else {
          const totalCapacity = memberData.reduce((total: number, member: any) => total + member.capacity, 0);
          const totalHours = memberData.reduce((total: number, member: any) => total + member.hoursWorked, 0);
          const billableHours = memberData.reduce((total: number, member: any) => total + member.billableHours, 0);
          
          finalStats = {
            totalHours: totalHours,
            teamCapacity: totalCapacity,
            billableHours: billableHours,
            nonBillableHours: 0,
            utilizationPercentage: totalCapacity > 0 ? (totalHours / totalCapacity) * 100 : 0
          };
        }

        const weekEndDate = new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
        setWeeklyData({
          weekStart: weekStartStr,
          weekEnd: weekEndDate.toISOString().split('T')[0],
          members: memberData,
          stats: finalStats
        });
      } else {
        // Fallback for empty/mock data
        const weekEndDate = new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
        setWeeklyData({
          weekStart: weekStartStr,
          weekEnd: weekEndDate.toISOString().split('T')[0],
          members: [],
          stats: {
            totalHours: 0,
            teamCapacity: 0,
            billableHours: 0,
            nonBillableHours: 0,
            utilizationPercentage: 0
          }
        });
      }
    } catch (error) {
      console.error('Failed to load team data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWeekStart]);

  // Load pending invitations
  const loadPendingInvitations = useCallback(async () => {
    try {
      setLoadingInvitations(true);
      const invitations = await apiService.getPendingInvitations();
      setPendingInvitations(invitations || []);
    } catch (error) {
      console.error('Failed to load pending invitations:', error);
      setPendingInvitations([]);
    } finally {
      setLoadingInvitations(false);
    }
  }, []);

  useEffect(() => {
    loadTeamData();
    loadPendingInvitations();
  }, [loadTeamData, loadPendingInvitations]);

  const handleResendInvitation = async (invitationId: string) => {
    try {
      setLoadingInvitations(true);
      await apiService.resendInvitation(invitationId);
      alert('Invitation email resent successfully!');
      await loadPendingInvitations();
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      alert('Failed to resend invitation');
    } finally {
      setLoadingInvitations(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string, email: string) => {
    if (!window.confirm(`Are you sure you want to cancel the invitation for ${email}?`)) return;
    try {
      setLoadingInvitations(true);
      await apiService.cancelInvitation(invitationId);
      alert('Invitation cancelled successfully!');
      await loadPendingInvitations();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      alert('Failed to cancel invitation');
    } finally {
      setLoadingInvitations(false);
    }
  };

  const formatWeekRange = (weekStart: Date) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    return `${timezoneService.formatUserDate(weekStart, { day: '2-digit', month: 'short' })} – ${timezoneService.formatUserDate(weekEnd, { day: '2-digit', month: 'short' })} ${weekStart.getFullYear()}`;
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    setCurrentWeekStart(newDate);
  };

  const handleCapacityChange = async (memberId: string, newCapacity: number) => {
    if (!weeklyData) return;
    try {
      await apiService.updateMemberCapacity(memberId, newCapacity);
      const updatedData = {
        ...weeklyData,
        members: weeklyData.members.map((memberData: any) => 
          memberData.member.id === memberId ? { ...memberData, capacity: newCapacity } : memberData
        )
      };
      // Updated accumulator logic to be correct TS
      updatedData.stats.teamCapacity = updatedData.members.reduce((total: number, memberData: any) => total + memberData.capacity, 0);
      setWeeklyData(updatedData);
    } catch (error) {
      console.error('Failed to update capacity:', error);
      alert('Failed to update capacity.');
    }
  };

  const handleInviteMember = async () => {
    try {
      setLoading(true);
      if (!inviteForm.email || !inviteForm.role || !inviteForm.capacity) {
        alert('Please fill in all fields');
        return;
      }
      await apiService.inviteTeamMember(inviteForm.email, inviteForm.role, inviteForm.capacity);
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'Member', capacity: 35 });
      await Promise.all([loadTeamData(), loadPendingInvitations()]);
      alert('Invitation sent successfully!');
    } catch (error) {
      console.error('Failed to invite member:', error);
      alert('Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleEditMember = (memberData: any) => {
    setEditingMember(memberData);
    setEditForm({ 
      role: memberData.member.role,
      costRate: memberData.member.costRate || '',
      billRate: memberData.member.billRate || '',
      currency: memberData.member.currency || 'USD'
    });
    setShowEditMemberDialog(true);
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;
    try {
      setLoading(true);
      
      // Update role and billing rates
      await apiService.updateTeamMember(editingMember.member.id, {
        role: editForm.role,
        costRate: editForm.costRate === '' ? null : Number(editForm.costRate),
        billRate: editForm.billRate === '' ? null : Number(editForm.billRate),
        currency: editForm.currency
      });
      
      setShowEditMemberDialog(false);
      setEditingMember(null);
      setEditForm({ role: 'member', costRate: '', billRate: '', currency: 'USD' });
      await loadTeamData();
      alert('Team member updated successfully!');
    } catch (error) {
      console.error('Failed to update team member:', error);
      alert('Failed to update team member');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberData: any) => {
    if (!window.confirm(`Are you sure you want to remove ${memberData.member.name} from the team?`)) return;
    try {
      setLoading(true);
      await apiService.deleteUser(memberData.member.id);
      await loadTeamData();
      alert(`${memberData.member.name} has been removed.`);
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to remove user');
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'super_owner': case 'workspace_owner': case 'owner': return 'error';
      case 'administrator': case 'admin': return 'warning';
      case 'member': return 'primary';
      case 'tester': return 'info';
      default: return 'default';
    }
  };

  const filteredMembers = (weeklyData?.members || []).filter((memberData: any) => {
    const matchesSearch = memberData.member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         memberData.member.email.toLowerCase().includes(searchTerm.toLowerCase());
    let matchesFilter = true;
    if (selectedFilter !== 'Everyone') {
      if (selectedFilter === 'Active') matchesFilter = memberData.member.isActive;
      else matchesFilter = memberData.member.role.toLowerCase() === selectedFilter.toLowerCase().slice(0, -1);
    }
    return matchesSearch && matchesFilter;
  });

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '24px'
    },
    metricsGridSmall: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '24px',
      marginBottom: '24px'
    },
    metricCard: {
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      height: '200px',
      display: 'flex',
      flexDirection: 'column' as const
    },
    metricHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px'
    },
    metricHeaderLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    metricTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    progressContainer: {
      marginTop: '16px'
    },
    progressLabel: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '8px'
    },
    chartCard: {
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '24px',
      overflow: 'visible'
    },
    chartTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    insightCard: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #e9ecef',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '16px',
      flex: 1
    },
    insightTitle: {
      fontSize: '14px',
      fontWeight: '600',
      marginBottom: '8px',
      color: '#495057'
    },
    insightText: {
      fontSize: '13px',
      color: '#6c757d',
      lineHeight: '1.4'
    },
  } as const; // Add as const to satisfy style requirements

  const utilizationRate = weeklyData?.stats?.utilizationPercentage || 0;
  const isHighLoad = utilizationRate > 85;
  const isLowLoad = utilizationRate < 50;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <People sx={{ fontSize: '32px', color: brandColors.primary }} />
        <h1 style={styles.title}>Team Management</h1>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={<VideoCall />}
            onClick={() => setShowHuddleModal(true)}
            sx={{ backgroundColor: '#2196f3', '&:hover': { backgroundColor: '#1976d2' } }}
          >
            Team Huddle
          </Button>
          <Button
            variant="contained"
            startIcon={<PersonAdd />}
            onClick={() => setShowInviteModal(true)}
            sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}
          >
            Invite Member
          </Button>
        </div>
      </div>
      
      <p style={styles.subtitle}>
        Monitor team performance, manage capacity, and gain insights into team dynamics.
      </p>

      {/* Overview Section */}
      <div style={styles.metricsGridSmall}>
        {/* Card 1: Team Performance */}
        <Card sx={styles.metricCard}>
          <div style={styles.metricHeader}>
            <div style={styles.metricHeaderLeft}>
              <TrendingUp sx={{ fontSize: '20px', color: brandColors.primary }} />
              <Typography sx={styles.metricTitle}>Team Performance</Typography>
            </div>
            {/* Week Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, backgroundColor: '#f5f5f5', borderRadius: 1, padding: 0.5 }}>
              <IconButton onClick={() => navigateWeek('prev')} size="small">
                <NavigateBefore fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ fontSize: '11px', fontWeight: 600 }}>
                {formatWeekRange(currentWeekStart)}
              </Typography>
              <IconButton onClick={() => navigateWeek('next')} size="small">
                <NavigateNext fontSize="small" />
              </IconButton>
            </Box>
          </div>
          
          <Typography sx={{ fontSize: '24px', fontWeight: '700', color: brandColors.primary, marginBottom: '4px' }}>
            {weeklyData?.stats?.totalHours?.toFixed(1) || '0.0'}h
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#666' }}>
            Total hours worked across {weeklyData?.members?.length || 0} members
          </Typography>

          <div style={styles.progressContainer}>
            <div style={styles.progressLabel}>
              <Typography sx={{ fontSize: '11px', color: '#666' }}>Utilization vs Capacity</Typography>
              <Typography sx={{ fontSize: '11px', color: '#666' }}>
                {utilizationRate.toFixed(0)}%
              </Typography>
            </div>
            <LinearProgress
              variant="determinate"
              value={Math.min(utilizationRate, 100)}
              sx={{
                height: '8px',
                borderRadius: '4px',
                backgroundColor: '#f0f0f0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: isHighLoad ? '#e74c3c' : (isLowLoad ? '#f39c12' : '#27ae60')
                }
              }}
            />
          </div>
        </Card>

        {/* Card 2: Team Insights */}
        <Card sx={styles.metricCard}>
          <div style={styles.metricHeader}>
            <div style={styles.metricHeaderLeft}>
              <Assessment sx={{ fontSize: '20px', color: brandColors.primary }} />
              <Typography sx={styles.metricTitle}>Team Insights</Typography>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '16px', height: '100%' }}>
            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>📈 Load Analysis</Typography>
              <Typography sx={styles.insightText}>
                {isHighLoad 
                  ? "Team is operating at high capacity. Consider redistributing tasks or hiring to prevent burnout." 
                  : isLowLoad 
                    ? "Team utilization is lower than expected. Check for blocked tasks or consider assigning more work."
                    : "Team is operating at a healthy capacity level. Keep up the good momentum."
                }
              </Typography>
            </div>
            <div style={styles.insightCard}>
              <Typography sx={styles.insightTitle}>⚡ Productivity Focus</Typography>
              <Typography sx={styles.insightText}>
                {weeklyData?.stats?.billableHours && weeklyData?.stats?.totalHours 
                  ? `${((weeklyData.stats.billableHours / weeklyData.stats.totalHours) * 100).toFixed(0)}% of tracked time is billable. Focus on minimizing non-billable meetings.`
                  : "Track billable hours to get insights on financial efficiency."
                }
              </Typography>
            </div>
          </div>
        </Card>
      </div>

      {/* Team Activity Timeline - Stacked Bar Chart */}
      <Card sx={styles.chartCard}>
        <div style={styles.chartTitle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Timeline sx={{ fontSize: '20px', color: brandColors.primary }} />
            <Typography sx={{ fontSize: '18px', fontWeight: '600' }}>Team Activity Timeline</Typography>
          </div>
          
          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxWidth: '60%', justifyContent: 'flex-end' }}>
            {weeklyData?.members?.slice(0, 5).map((m: any, i: number) => (
              <Box key={m.member.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: TEAM_COLORS[i % TEAM_COLORS.length] }} />
                <Typography variant="caption" color="text.secondary">{m.member.name.split(' ')[0]}</Typography>
              </Box>
            ))}
          </Box>
        </div>
        
        {/* Stacked Bar Chart */}
        {(() => {
          const weekDays = Array.from({ length: 7 }, (_, i) => {
            const d = new Date(currentWeekStart);
            d.setDate(d.getDate() + i);
            return d;
          });

          // Calculate daily totals for scaling
          const dailyTotals = weekDays.map(dayDate => {
            const dateStr = DateService.dateToUserDateString(dayDate);
            return weeklyData?.members?.reduce((sum: number, member: any) => {
              const dayStat = member.dailyStats?.find((ds: any) => ds.date === dateStr);
              return sum + (dayStat ? Number(dayStat.hours) : 0);
            }, 0) || 0;
          });

          const maxDailyHours = Math.max(...dailyTotals, 8);

          return (
            <Box sx={{ height: 240, position: 'relative', mt: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                height: '100%', 
                alignItems: 'flex-end', 
                justifyContent: 'space-between',
                gap: 2,
                px: 1 
              }}>
                {/* Y-Axis Grid */}
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 30, zIndex: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  {[100, 75, 50, 25, 0].map((tick) => (
                    <Box key={tick} sx={{ borderTop: tick === 0 ? '1px solid #e0e0e0' : '1px dashed #f0f0f0', width: '100%', height: '1px', position: 'relative' }}>
                      {tick > 0 && <Typography variant="caption" sx={{ position: 'absolute', top: -10, left: -25, color: '#999', fontSize: '10px' }}>{Math.round(maxDailyHours * (tick/100))}h</Typography>}
                    </Box>
                  ))}
                </Box>

                {weekDays.map((dayDate, index) => {
                  const dateStr = DateService.dateToUserDateString(dayDate);
                  const isToday = DateService.isSameDay(dayDate, new Date());
                  
                  return (
                    <Box key={index} sx={{ 
                      flex: 1, 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center', 
                      height: '100%', 
                      zIndex: 1,
                      position: 'relative' 
                    }}>
                      <Box sx={{ 
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column-reverse', // Stack from bottom
                        alignItems: 'center',
                        position: 'relative',
                        paddingBottom: '30px'
                      }}>
                        {weeklyData?.members?.map((member: any, mIndex: number) => {
                          const dayStat = member.dailyStats?.find((ds: any) => ds.date === dateStr);
                          const hours = dayStat ? Number(dayStat.hours) : 0;
                          if (hours === 0) return null;
                          
                          const heightPercentage = (hours / maxDailyHours) * 100;
                          
                          return (
                            <Tooltip 
                              key={member.member.id}
                              title={`${member.member.name}: ${hours.toFixed(1)}h (${timezoneService.formatUserDate(dayDate)})`}
                              arrow
                            >
                              <Box sx={{ 
                                width: '80%', 
                                height: `${heightPercentage}%`, 
                                backgroundColor: TEAM_COLORS[mIndex % TEAM_COLORS.length],
                                borderTop: '1px solid white',
                                '&:first-of-type': { borderRadius: '0 0 4px 4px' }, // Bottom (in reversed column) requires top radius visual? No, bottom is first.
                                '&:last-child': { borderRadius: '4px 4px 0 0' }, // Top
                                transition: 'all 0.2s',
                                '&:hover': { opacity: 0.8 }
                              }} />
                            </Tooltip>
                          );
                        })}
                      </Box>
                      
                      {/* X-Axis Label */}
                      <Box sx={{ position: 'absolute', bottom: 0, textAlign: 'center', width: '100%' }}>
                        <Typography variant="caption" sx={{ 
                          display: 'block', 
                          fontWeight: isToday ? 'bold' : 'normal',
                          color: isToday ? brandColors.primary : '#666',
                          fontSize: '11px'
                        }}>
                          {timezoneService.formatUserDate(dayDate, { weekday: 'short' })}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', fontSize: '10px', color: '#999' }}>
                          {dayDate.getDate()}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })()}
      </Card>

      {/* Team Members */}
      <Card sx={styles.chartCard} style={{ padding: 0, overflow: 'hidden' }}>
        <Box sx={{ p: 3, paddingBottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            👥 Team Members ({filteredMembers.length})
          </Typography>
          
          <TextField
            placeholder="Search members..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
        </Box>

        {(weeklyData?.members?.length || 0) === 0 ? (
          <Alert severity="info" sx={{ m: 3 }}>
            <AlertTitle>No Team Members</AlertTitle>
            <Typography variant="body2">
              No team members found. Invite your first team member to get started with collaboration.
            </Typography>
          </Alert>
        ) : null}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <People sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Member
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Role
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Hours Worked
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Capacity
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Billable Hours
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Utilization
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Actions
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMembers.map((memberData: any) => (
                <TableRow key={memberData.member.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src={`https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                        alt={memberData.member.name}
                        sx={{ width: 40, height: 40 }}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                          {memberData.member.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {memberData.member.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={memberData.member.role}
                      size="small"
                      color={getRoleColor(memberData.member.role) as any}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {memberData.hoursWorked.toFixed(1)}h
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <CapacityInput
                      value={memberData.capacity}
                      onChange={(newValue) => handleCapacityChange(memberData.member.id, newValue)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {memberData.billableHours.toFixed(1)}h
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(memberData.utilizationPercentage, 100)}
                        sx={{
                          width: 60,
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: '#f0f0f0',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: memberData.utilizationPercentage > 80 ? '#e74c3c' : 
                                            memberData.utilizationPercentage > 60 ? '#f39c12' : '#27ae60',
                          }
                        }}
                      />
                      <Typography variant="caption" sx={{ minWidth: 35 }}>
                        {memberData.utilizationPercentage.toFixed(0)}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Edit member">
                      <IconButton size="small" onClick={() => handleEditMember(memberData)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove member">
                      <IconButton size="small" color="error" onClick={() => handleDeleteMember(memberData)}>
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {filteredMembers.length === 0 && (weeklyData?.members?.length || 0) > 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No members match your search "{searchTerm}". Try a different search term.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Invite Member Dialog */}
      <Dialog
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAdd />
            Invite Team Member
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email Address"
              type="email"
              required
              fullWidth
              placeholder="colleague@company.com"
              value={inviteForm.email}
              onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
            />

            <FormControl fullWidth required>
              <InputLabel>Role</InputLabel>
              <Select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="Member">Member</MenuItem>
                <MenuItem value="Tester">Tester</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Owner">Owner</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Weekly Capacity (hours)"
              type="number"
              required
              fullWidth
              placeholder="35"
              inputProps={{ min: 0, max: 80, step: 0.5 }}
              value={inviteForm.capacity}
              onChange={(e) => setInviteForm({ ...inviteForm, capacity: parseFloat(e.target.value) || 0 })}
              helperText="Recommended: 35-40 hours per week"
            />

            <Alert severity="info">
              <AlertTitle>Team Invitation</AlertTitle>
              <Typography variant="body2">
                The invited member will receive an email with instructions to join your team.
                They will have access based on the role you assign.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowInviteModal(false);
              setInviteForm({ email: '', role: 'Member', capacity: 35 });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleInviteMember}
            variant="contained"
            disabled={loading || !inviteForm.email || !inviteForm.role || !inviteForm.capacity}
            sx={{
              backgroundColor: '#10b981',
              '&:hover': {
                backgroundColor: '#059669',
              },
            }}
          >
            {loading ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog
        open={showEditMemberDialog}
        onClose={() => {
          setShowEditMemberDialog(false);
          setEditingMember(null);
          setEditForm({ role: 'member', costRate: '', billRate: '', currency: 'USD' });
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit />
            Edit Team Member
          </Box>
        </DialogTitle>
        <DialogContent>
          {editingMember && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  src={`https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face`}
                  alt={editingMember.member.name}
                  sx={{ width: 48, height: 48 }}
                />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {editingMember.member.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {editingMember.member.email}
                  </Typography>
                </Box>
              </Box>

              <FormControl fullWidth required>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editForm.role}
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  label="Role"
                >
                  <MenuItem value="member">Member</MenuItem>
                  <MenuItem value="tester">Tester</MenuItem>
                  <MenuItem value="admin">Administrator</MenuItem>
                  <MenuItem value="owner">Owner</MenuItem>
                </Select>
              </FormControl>

              {/* Billing Rates Section */}
              <Box sx={{ 
                p: 2, 
                backgroundColor: '#f8f9fa', 
                borderRadius: 1,
                border: '1px solid #e0e0e0'
              }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                  💰 Billing Rates (Optional)
                </Typography>
                
                <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                  Leave empty for internal members (hours only). Fill in for billable members.
                </Typography>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                  <FormControl size="small">
                    <InputLabel>Currency</InputLabel>
                    <Select
                      value={editForm.currency}
                      onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                      label="Currency"
                    >
                      <MenuItem value="USD">USD ($)</MenuItem>
                      <MenuItem value="SEK">SEK (kr)</MenuItem>
                      <MenuItem value="EUR">EUR (€)</MenuItem>
                      <MenuItem value="NOK">NOK (kr)</MenuItem>
                      <MenuItem value="DKK">DKK (kr)</MenuItem>
                      <MenuItem value="GBP">GBP (£)</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    label="Cost Rate/h"
                    type="number"
                    size="small"
                    value={editForm.costRate}
                    onChange={(e) => setEditForm({ ...editForm, costRate: e.target.value })}
                    placeholder="0"
                    inputProps={{ min: 0, step: 1 }}
                    helperText="Your cost"
                  />
                  
                  <TextField
                    label="Bill Rate/h"
                    type="number"
                    size="small"
                    value={editForm.billRate}
                    onChange={(e) => setEditForm({ ...editForm, billRate: e.target.value })}
                    placeholder="0"
                    inputProps={{ min: 0, step: 1 }}
                    helperText="Client rate"
                  />
                </Box>

                {(editForm.costRate || editForm.billRate) ? (
                  <Chip 
                    label="Billable" 
                    size="small" 
                    color="success" 
                    sx={{ mt: 2 }}
                  />
                ) : (
                  <Chip 
                    label="Internal (hours only)" 
                    size="small" 
                    variant="outlined"
                    sx={{ mt: 2 }}
                  />
                )}
              </Box>

              <Alert severity="info">
                <AlertTitle>Role Permissions</AlertTitle>
                <Typography variant="body2">
                  <strong>Member:</strong> Can track time, view reports<br/>
                  <strong>Tester:</strong> Same as member + testing features<br/>
                  <strong>Administrator:</strong> Can manage team and settings<br/>
                  <strong>Owner:</strong> Full access to all features
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowEditMemberDialog(false);
              setEditingMember(null);
              setEditForm({ role: 'member', costRate: '', billRate: '', currency: 'USD' });
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateMember}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: brandColors.primary,
              '&:hover': {
                backgroundColor: brandColors.primary,
              },
            }}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Huddle Modal */}
      <HuddleModal
        open={showHuddleModal}
        onClose={() => {
          console.log('🎥 Closing huddle modal');
          setShowHuddleModal(false);
        }}
        channelId={huddleChannelId}
        channelName={huddleChannelName}
        participants={filteredMembers.map((memberData: any) => ({
          id: memberData.member.id,
          name: memberData.member.name,
          email: memberData.member.email,
          avatar: memberData.member.avatar,
          isActive: memberData.member.isActive
        }))}
      />
    </div>
  );
};

export default TeamManager;
